<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $t('HeadContract.DelaysSchedule.DelaySaveModal.title') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="h-full w-full">
        <form
          class="flex flex-col space-y-4"
          @submit.prevent="saveDelay"
        >
          <div class="font-semibold">
            {{ $t('HeadContract.DelaysSchedule.DelaySaveModal.todayDate') }} {{ new Date().toLocaleDateString() }}
          </div>

          <label>
            {{ $t('HeadContract.DelaysSchedule.DelaySaveModal.label.dateOfOccurrence') }}

            <AppInput
              v-model="delayForm.form.date_of_occurrence"
              :error="delayForm.getErrors('date_of_occurrence')"
              class="form-input"
              type="date"
              name="date_of_occurrence"
            />
          </label>

          <label>
            Start Date

            <AppInput
              v-model="delayForm.form.start_date"
              :error="delayForm.getErrors('start_date')"
              class="form-input"
              type="date"
              name="start_date"
            />
          </label>

          <label>
            End Date

            <AppInput
              v-model="delayForm.form.end_date"
              :error="delayForm.getErrors('end_date')"
              class="form-input"
              type="date"
              name="end_date"
            />
          </label>

          <div>
            {{ $t('HeadContract.DelaysSchedule.DelaySaveModal.label.description') }}
            <AppTextArea
              v-model="delayForm.form.description"
              :error="delayForm.getErrors('description')"
              name="description"
              class="form-input resize-none"
            />
          </div>

          <div>
            {{ $t('HeadContract.DelaysSchedule.DelaySaveModal.label.notes') }}
            <AppTextArea
              v-model="delayForm.form.notes"
              :error="delayForm.getErrors('notes')"
              name="notes"
              class="form-input resize-none"
            />
          </div>

          <FileUpload
            v-model="delayForm.form.images"
            multiple
            @change="addFile"
          />

          <span
            v-if="filesUploaded.length"
            class="font-semibold"
          >
            {{ $t('HeadContract.DelaysSchedule.DelaySaveModal.attachments') }}
          </span>

          <div
            v-if="filesUploaded.length"
            class="overflow-x-auto max-w-sm flex"
          >
            <UploadedFileDisplay
              v-for="(file, i) in filesUploaded"
              :key="`${file.fileName}-${i}`"
              :file="file"
              :error="delayForm.getErrors(`images.${i}`)"
              @delete="removeFile(i)"
            />
          </div>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close')"
        >
          {{ $t('HeadContract.DelaysSchedule.DelaySaveModal.button.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          data-test="apply-filter"
          @click="saveDelay"
        >
          {{ $t('HeadContract.DelaysSchedule.DelaySaveModal.button.create') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import Modal from '@/components/Modal.vue';
import UploadedFileDisplay from '@/components/UploadedFileDisplay.vue';
import { useFileUploadForm } from '@/composables/useFileUploadForm';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput, getFileMime } from '@/helpers';
import { useDelaysStore } from '@/store/delays';
import { ref } from 'vue';

const props = defineProps<{
  projectId: string;
  delayId?: string;
}>();

const emits = defineEmits<{
  (e: 'close');
}>();

const delaysStore = useDelaysStore();
const createDelayAction = useStoreApiAction(delaysStore.createDelay);

const delayForm = useFormData({
  description: '',
  notes: '',
  images: [],
  date_of_occurrence: dateForDateInput(new Date()),
  start_date: '',
  end_date: '',
});

const saveDelay = () => {
  createDelayAction.request(props.projectId, delayForm.toFormDataObject()).then((delay) => {
    emits('close');
  }).catch((error) => {
    delayForm.setErrorsFromAxiosError(error);
  });
};

const { filesUploaded, addFile, removeFile } = useFileUploadForm(undefined, (file, index) => {
  delayForm.form.images.splice(index, 1);
});
</script>

<style scoped></style>
